import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { settings } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Skeleton } from '@/shared/ui/Skeleton';
import { statusLabel } from '@/shared/ui/statusLabels';
import { useToast } from '@/shared/ui/useToast';

/**
 * Prazo acordado para cada etapa da esteira.
 *
 * E este numero que faz a fila de demandas dizer "vencido". Antes ele era fixo no
 * codigo, igual para toda empresa, e ninguem podia ajustar o proprio acordo.
 *
 * O prazo e em dias uteis. Dias corridos fariam o prazo de uma operacao aberta na
 * sexta vencer no domingo, quando ninguem trabalhou.
 */
export function StageSlaPanel() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const podeVer = can('configuracoes.sla.view');
  const podeEditar = can('configuracoes.sla.update');

  const [rascunho, setRascunho] = useState<Record<string, string>>({});

  const consulta = useQuery({
    queryKey: ['stage-slas'],
    queryFn: ({ signal }) => settings.stageSlas(signal),
    enabled: podeVer,
  });

  const salvar = useMutation({
    mutationFn: ({ stage, dias }: { stage: string; dias: number }) =>
      settings.setStageSla(stage, dias),
    onSuccess: (prazo) => {
      toast.success(
        `Prazo de ${statusLabel('operacao', prazo.stage).toLowerCase()} definido.`,
      );
      setRascunho((atual) => {
        const proximo = { ...atual };
        delete proximo[prazo.stage];
        return proximo;
      });
      void queryClient.invalidateQueries({ queryKey: ['stage-slas'] });
      // A fila de demandas usa o prazo para marcar o vencido: ela precisa reler.
      void queryClient.invalidateQueries({ queryKey: ['demands'] });
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  if (!podeVer) return null;

  return (
    <Card>
      <CardHeader
        title="Prazo por etapa"
        description="Dias uteis acordados para cada etapa da esteira."
      />
      <CardBody className="flex flex-col gap-3">
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar os prazos">
            {consulta.error.message}
          </Alert>
        ) : null}

        {consulta.isPending ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 8 }, (_, indice) => (
              <Skeleton key={indice} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
            {(consulta.data ?? []).map((prazo) => {
              const valor = rascunho[prazo.stage] ?? String(prazo.business_days || '');
              const alterado =
                rascunho[prazo.stage] !== undefined &&
                Number(rascunho[prazo.stage]) !== prazo.business_days;

              return (
                <li key={prazo.stage} className="flex items-center gap-3 px-3 py-2.5">
                  <span className="min-w-0 flex-1 truncate text-sm text-ink-700">
                    {statusLabel('operacao', prazo.stage)}
                  </span>

                  {prazo.business_days === 0 ? (
                    <span className="shrink-0 text-2xs text-ink-400">sem prazo</span>
                  ) : null}

                  <div className="w-24 shrink-0">
                    <Field
                      label={`Dias uteis em ${statusLabel('operacao', prazo.stage)}`}
                      labelHidden
                      type="number"
                      min="1"
                      max="90"
                      className="h-9"
                      disabled={!podeEditar}
                      value={valor}
                      onChange={(evento) =>
                        setRascunho((atual) => ({
                          ...atual,
                          [prazo.stage]: evento.target.value,
                        }))
                      }
                    />
                  </div>

                  {podeEditar ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!alterado || salvar.isPending}
                      onClick={() =>
                        salvar.mutate({
                          stage: prazo.stage,
                          dias: Number(rascunho[prazo.stage]),
                        })
                      }
                    >
                      Salvar
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {!podeEditar ? (
          <p className="text-2xs text-ink-500">
            Somente leitura: o seu acesso nao inclui a definicao de prazos.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
